import { PropsWithChildren, useState } from 'react';

import cn from 'classnames';
import { Reorder, useDragControls } from 'motion/react';

import { CloseIcon, colors, Divider, HandleVerticalIcon, PlusIcon, Spinner, u } from '@ui';

import * as css from './ModuleReorder.css';

type ModuleReorderGroupProps = {
    isEditing?: boolean;
    ids: string[];
    onReorder: (ids: string[]) => void;
    isLoading?: boolean;
};

export const ModuleReorderGroup = ({
    children,
    isEditing,
    onReorder,
    ids,
    isLoading,
}: PropsWithChildren<ModuleReorderGroupProps>) =>
    isEditing ? (
        <div className={css.reorderBoundary}>
            {isLoading && (
                <div className={css.reorderLoader}>
                    <Spinner size="m" />
                </div>
            )}
            <Reorder.Group
                axis="y"
                className={cn(css.layoutModules, isLoading && css.reorderModulesLoading)}
                values={ids}
                onReorder={onReorder}
            >
                {children}
            </Reorder.Group>
        </div>
    ) : (
        <div className={css.layoutModules}>{children}</div>
    );

type ModuleReorderItemProps = {
    isEditing?: boolean;
    value: string;
    onRemove: () => void;
    onAdd: () => void;
};

export const ModuleReorderItem = ({
    children,
    isEditing,
    value,
    onRemove,
    onAdd,
}: PropsWithChildren<ModuleReorderItemProps>) => {
    const dragControls = useDragControls();

    const [isDragging, setIsDragging] = useState(false);
    return isEditing ? (
        <Reorder.Item
            value={value}
            dragControls={dragControls}
            className={css.reorderRoot}
            onDragStart={() => setIsDragging(true)}
            onDragEnd={() => setIsDragging(false)}
            transition={{
                type: 'tween',
                duration: 0.15,
            }}
        >
            <div className={cn(css.dividerRoot, isDragging && css.controlsHidden)}>
                <Divider color={colors.blue[200]} />
                <div className={css.dividerButton} onClick={onAdd}>
                    <PlusIcon size={u(2)} weight="bold" />
                </div>
            </div>
            <div className={css.handleRoot}>
                <div className={css.handle}>
                    <HandleVerticalIcon size={u(3)} />
                </div>
                <div
                    className={cn(css.handleRemove, isDragging && css.controlsHidden)}
                    onClick={onRemove}
                    hidden={isDragging}
                >
                    <CloseIcon size={u(2)} weight="bold" />
                </div>
                {children}
            </div>
        </Reorder.Item>
    ) : (
        children
    );
};

export const ModuleAddItem = ({
    isEditing,
    onAdd,
}: PropsWithChildren<Pick<ModuleReorderItemProps, 'isEditing' | 'onAdd'>>) => {
    return (
        isEditing && (
            <div className={css.dividerRoot}>
                <Divider color={colors.blue[200]} />
                <div className={css.dividerButton} onClick={onAdd}>
                    <PlusIcon size={u(2)} weight="bold" />
                </div>
            </div>
        )
    );
};
